import type { Block } from '@/types/blocks'
import { sortByOrder } from './orderUtils'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

/**
 * Структурный вид блока, а не `Block`: тот же признак читает панель редактора
 * (`VideoBlockEditor`), у которой на руках черновик `VideoBlock & { id }` —
 * без `createdAt`/`updatedAt`. Сузить признак до `Block` значило бы завести в
 * панели ВТОРУЮ копию условия, ровно ту, ради которой этот файл и заведён.
 */
export type MaybeVideoBlock = {
  type: Block['type']
  videoId?: string | null
  videoIdEn?: string | null
}

/**
 * Место блока — ПОЗИЦИОННЫЕ ранги (0-based), а не `order`: `order` в проекте
 * тоже 0-based и вдобавок идёт с дырами (удаление соседа не перенумеровывает
 * список), а весь интерфейс считает с единицы — `LessonPill` печатает
 * `lessonIndex + 1`, `BlockThumbnail`/`screenDeleteConfirm` — `index + 1`.
 * Печать сырого `order` давала несуществующие «Урок 0, экран 0».
 */
export interface EmptyVideoLocation {
  lessonRank: number
  blockRank: number
}

interface LessonWithBlocks {
  id: number
  /** Позиция урока в `course.lessons` — тот же счёт, что у плашек урока. */
  rank: number
  blocks: Block[]
}

/**
 * Видео-блок без ролика (бэк-лог заказчика, п.19): пустой слот — и `null`,
 * и `''`. Пусты обязаны быть ОБА слота: у курса с `sourceLang: 'en'`
 * оригинальный ролик лежит в `videoIdEn` (превью показывает его симметричным
 * фолбэком), и признак, знающий только `videoId`, запрещал такому курсу
 * публикацию навсегда.
 *
 * ЕДИНСТВЕННЫЙ источник этого признака: его же читают гейт публикации и
 * баннер видео-блока — второй копии условия не заводить. Бэк держит то же
 * правило (`_in_any_slot` в валидаторе api).
 */
export const isEmptyVideoBlock = (block: MaybeVideoBlock): boolean =>
  block.type === 'video' && !block.videoId && !block.videoIdEn

/**
 * Места видео-блоков без ролика. Гейт публикации курса (`PublishDialog`) не
 * даёт уйти в каталог с таким блоком — `oa-mobile` зовёт `useGetVideoQuery`
 * без `skip` и на пустом id уходит опрашивать `videos/undefined` каждые 5
 * секунд бесконечно (веб-плеер защищён, мобильный — нет).
 */
export function findEmptyVideoBlocks(lessons: LessonWithBlocks[]): EmptyVideoLocation[] {
  const found: EmptyVideoLocation[] = []
  for (const lesson of lessons) {
    // Тот же порядок, что видит лента блоков (`sortByOrder` в `getLessonBlocks`),
    // — позиция обязана совпадать с номером на миниатюре.
    sortByOrder(lesson.blocks).forEach((block, blockRank) => {
      if (isEmptyVideoBlock(block)) found.push({ lessonRank: lesson.rank, blockRank })
    })
  }
  return found
}

/**
 * Отказ в публикации из-за пустых видео-блоков — текст уведомления в момент
 * попытки (постоянной плашки нет, решение заказчика). Номера позиционные и с
 * единицы, как на плашке урока и миниатюре. `null` — пустых нет.
 */
export const emptyVideoRefusal = (empties: EmptyVideoLocation[]): string | null =>
  empties.length === 0
    ? null
    : messagesNow(messages).emptyVideoRefusal(
        empties.map((e) => ({ lesson: e.lessonRank + 1, block: e.blockRank + 1 })),
      )
